import { createFileRoute, notFound } from "@tanstack/react-router";

import { JsonLd } from "@/components/layout/JsonLd";
import { BlogPostView } from "@/components/pages/BlogPostView";
import { blog } from "@/content/blog";
import { meta } from "@/content/meta";
import { isLocale } from "@/i18n/locales";
import { absoluteUrl, canonicalFor, SITE_NAME, seoHead } from "@/lib/site";

export const Route = createFileRoute("/$locale/blog/$slug")({
  loader: ({ params }) => {
    if (!isLocale(params.locale)) throw notFound();
    const post = blog[params.locale].find((item) => item.slug === params.slug);
    if (!post) throw notFound();
    return { post };
  },
  head: ({ params, loaderData }) => {
    if (!isLocale(params.locale) || !loaderData) return {};
    const title = meta[params.locale].blogPost.title.replace(
      "{title}",
      loaderData.post.title,
    );
    return seoHead({
      locale: params.locale,
      path: `/blog/${loaderData.post.slug}`,
      title,
      description:
        loaderData.post.excerpt || meta[params.locale].blogPost.description,
      image: loaderData.post.image,
      type: "article",
      published: loaderData.post.date,
    });
  },
  component: BlogPostRoute,
});

function BlogPostRoute() {
  const { locale } = Route.useParams();
  const { post } = Route.useLoaderData();
  if (!isLocale(locale)) throw notFound();

  return (
    <>
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          headline: post.title,
          description: post.excerpt,
          image: absoluteUrl(post.image),
          author: { "@type": "Organization", name: post.author },
          publisher: {
            "@type": "Organization",
            name: SITE_NAME,
            logo: { "@type": "ImageObject", url: absoluteUrl("/og.png") },
          },
          datePublished: post.date,
          dateModified: post.date,
          mainEntityOfPage: canonicalFor(locale, `/blog/${post.slug}`),
          isAccessibleForFree: true,
          inLanguage: locale,
        }}
      />
      {"faq" in post && post.faq ? (
        <JsonLd
          data={{
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: post.faq.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          }}
        />
      ) : null}
      <BlogPostView locale={locale} post={post} />
    </>
  );
}
